/**
 * Which capture profile governs a URL, decided once for the extension and the
 * API (TOADO-452).
 *
 * The rule is BACKEND_PRD §2.13: longest prefix wins, so a narrow rule for one
 * site overrides a broad default in either direction. It lived in two copies
 * and they drifted: the extension moved to longest-prefix in TOADO-197 while
 * the API kept picking the SHORTEST match, with a docstring defending it. So a
 * `never` rule for one vendor portal under an `https://*` `full` default was
 * obeyed by the extension and ignored by the server, which stored bodies and
 * screenshots the workspace had said must not leave the browser. One copy, in
 * the package both already import, is what keeps that from happening twice.
 */

export type CaptureProfileMode = 'full' | 'metadata_only' | 'never';

/** Most restrictive first. */
const RESTRICTIVENESS: Record<CaptureProfileMode, number> = { never: 2, metadata_only: 1, full: 0 };

/**
 * A mode as this build understands it. A value it does not know is treated as
 * `never`, the most restrictive it has: a newer server's stricter mode must not
 * be read by an older extension as permission to collect everything. A server
 * that adds a mode keeps sending older extensions one they know
 * (docs/extension/EXTENSION_API_CONTRACT.md).
 */
export function captureProfileMode(value: unknown): CaptureProfileMode {
  return value === 'full' || value === 'metadata_only' || value === 'never' ? value : 'never';
}

/**
 * A URL in one spelling, split where matching needs it: `base` is the scheme
 * and host, `rest` the path and query. The fragment is never part of it: see
 * `patternOf` for why a rule cannot depend on one.
 */
type CanonicalParts = { base: string; rest: string };

const DEFAULT_PORTS: Record<string, string> = { http: ':80', https: ':443' };

/** RFC 3986 unreserved characters: an escaped one means the character itself. */
const UNRESERVED = /[A-Za-z0-9\-._~]/;

/**
 * Percent-escapes in one spelling (RFC 3986 §6.2.2): an escaped unreserved
 * character is decoded, and every other escape gets upper-case hex. Without
 * this `/%70rivate` is not `/private` to a prefix match, though it is to every
 * server that serves it. `%2F` stays an escape: whether a server reads it as
 * a slash is the server's choice, and a rule for `/private/*` does not cover
 * `/private%2Fx` (TOADO-469 records this as accepted).
 */
function normalizeEscapes(text: string): string {
  return text.replace(/%([0-9A-Fa-f]{2})/g, (_escape, hex: string) => {
    const character = String.fromCharCode(parseInt(hex, 16));
    return UNRESERVED.test(character) ? character : `%${hex.toUpperCase()}`;
  });
}

/**
 * A whole http(s) URL as the browser's own parser reads it (TOADO-468): dot
 * segments resolved, backslashes read as slashes, tabs and newlines dropped,
 * the host decoded, lowercased and stripped of a trailing dot, userinfo, the
 * default port and the fragment removed. Null for anything the parser refuses
 * or that is not http(s).
 *
 * The match used to be on raw strings, so `https://SSO.VENDOR.COM/login`, a URL
 * with userinfo, or one with an explicit default port missed a `never` rule
 * written as `https://sso.vendor.com/*` and fell under a broader `full` one.
 * TOADO-460 lowercased the scheme and host by hand, which left everything the
 * parser also rewrites: `/public/../private/secret` still missed a rule for
 * `/private/*`, and was then stored as `/private/secret`. The path and query
 * keep their case - they are case-sensitive, and a pattern may mean it.
 */
function parseWhole(value: string): CanonicalParts | null {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  url.username = '';
  url.password = '';
  url.hash = '';
  // Trailing dots counted off, not matched with `/\.+$/`, which reads a run of
  // dots again from every dot in it: a hostname of many dots and a letter took
  // seconds, and the API matches an uploaded URL (security review round 14).
  let hostEnd = url.hostname.length;
  while (hostEnd > 0 && url.hostname.charCodeAt(hostEnd - 1) === 46) hostEnd -= 1;
  const host = `${url.hostname.slice(0, hostEnd)}${url.port ? `:${url.port}` : ''}`;
  return {
    base: `${url.protocol}//${host}`,
    rest: normalizeEscapes(url.href.slice(`${url.protocol}//${url.host}`.length)),
  };
}

/** Scheme and authority only, by hand: for what the parser cannot take. */
function lexicalAuthority(scheme: string, authority: string, complete: boolean): string {
  let host = normalizeEscapes(authority.slice(authority.lastIndexOf('@') + 1)).toLowerCase();
  const defaultPort = DEFAULT_PORTS[scheme];
  // Only a complete authority loses its default port: `example.com:443*` with
  // the port stripped would be `example.com*`, which matches example.com.evil.net.
  if (complete && defaultPort && host.endsWith(defaultPort)) host = host.slice(0, -defaultPort.length);
  return `${scheme}://${host}`;
}

const SCHEME = /^([a-z][a-z0-9+.-]*):\/\//i;
/** Where an authority ends; a backslash is a slash to the URL parser (TOADO-468). */
const AUTHORITY_END = /[/?#\\]/;

/** What the parser cannot read is split by hand, as before TOADO-468. */
function lexicalWhole(value: string): CanonicalParts | null {
  const head = SCHEME.exec(value);
  if (!head) return null;
  const scheme = head[1]!.toLowerCase();
  const after = value.slice(head[0].length);
  const end = after.search(AUTHORITY_END);
  const authority = end < 0 ? after : after.slice(0, end);
  const tail = end < 0 ? '' : after.slice(end);
  const hashAt = tail.indexOf('#');
  return { base: lexicalAuthority(scheme, authority, true), rest: hashAt < 0 ? tail : tail.slice(0, hashAt) };
}

function wholeParts(value: string): CanonicalParts | null {
  return parseWhole(value) ?? lexicalWhole(value.replace(/[\t\n\r]/g, ''));
}

/**
 * A pattern, read once: its canonical text, whether it is a prefix, and
 * whether it named a fragment.
 */
type CanonicalPattern = { text: string; prefix: boolean; namedFragment: boolean };

/**
 * The prefix of a pattern, which may stop anywhere, even inside the host.
 *
 * Parsing a prefix that stops inside its authority as a URL would add a slash
 * and change where it stops, so only its scheme and authority are tidied, and
 * its port kept (TOADO-468) - with one exception: a prefix that ends with the
 * scheme's own default port means that host, since no URL spells it.
 *
 * Once the authority is complete the rest is read as the URL it begins, the
 * same way the URLs it is compared with are - but only as far as it goes
 * (TOADO-469). Parsed bare, the parser rewrote its end: `https://example.com/.*`
 * became the whole host and `/foo *` lost its space and matched `/foobar`. A
 * `~` is parsed after it and taken off again, so the end is never a segment or
 * a space the parser would tidy.
 */
function canonicalPrefix(raw: string): string {
  const head = SCHEME.exec(raw);
  if (!head) return raw;
  const scheme = head[1]!.toLowerCase();
  const after = raw.slice(head[0].length);
  if (after.search(AUTHORITY_END) < 0) {
    const defaultPort = DEFAULT_PORTS[scheme];
    if (!defaultPort || !after.endsWith(defaultPort)) return lexicalAuthority(scheme, after, false);
    return canonicalPrefix(`${raw}/`);
  }
  const parts = wholeParts(`${raw}~`);
  const text = parts ? `${parts.base}${parts.rest}` : '';
  return text.endsWith('~') ? text.slice(0, -1) : raw;
}

/** The pattern up to any `#`, and whether there was one. */
function withoutFragment(raw: string): { text: string; namedFragment: boolean } {
  const head = SCHEME.exec(raw);
  const hashAt = raw.indexOf('#', head ? head[0].length : 0);
  return hashAt < 0 ? { text: raw, namedFragment: false } : { text: raw.slice(0, hashAt), namedFragment: true };
}

/**
 * A rule can say nothing about a fragment (TOADO-469). The browser never sends
 * one, and the extension strips it before upload, so the server sees every URL
 * without it. When the matcher compared fragments, the extension (which has
 * the tab's) and the server (which has not) chose different profiles for the
 * same capture: a `#/billing` `never` rule the extension obeyed and the server
 * ignored, which left 0.1.4 installs with no backstop, and a `#/public` `full`
 * rule under a `never` default that the extension allowed and the server
 * refused, so the capture was dropped.
 *
 * So both sides match without the fragment, and a legacy rule that names one
 * can only make things stricter: it applies to the page its prefix names, and
 * only where it is stricter than what the other rules decide there (see
 * `matchCaptureProfile`). New rules with a `#` are refused where they are
 * written (`originPatternProblem`).
 */
/**
 * A profile's pattern as the matcher reads it, parsed once per profile.
 *
 * Matching walks every profile for every address, and parsing is URL work
 * (second review). Kept against the profile object rather than in a bounded
 * table of strings (review round 8): a workspace with more patterns than the
 * table held emptied it on every pass and parsed everything again, and a
 * WeakMap has no size to fall off and goes when the profile does. The source
 * string is kept beside it, so a profile whose pattern is changed in place is
 * parsed again. Results are shared, so no caller may change one.
 */
const parsedByProfile = new WeakMap<object, { source: string; pattern: CanonicalPattern }>();

function patternOfProfile(profile: { originPattern: string }): CanonicalPattern {
  const known = parsedByProfile.get(profile);
  if (known && known.source === profile.originPattern) return known.pattern;
  const pattern = patternOf(profile.originPattern);
  parsedByProfile.set(profile, { source: profile.originPattern, pattern });
  return pattern;
}

function patternOf(originPattern: string): CanonicalPattern {
  const prefix = originPattern.endsWith('*');
  const { text: raw, namedFragment } = withoutFragment(
    (prefix ? originPattern.slice(0, -1) : originPattern).replace(/[\t\n\r]/g, ''),
  );
  if (prefix) return { text: canonicalPrefix(raw), prefix, namedFragment };
  const parts = wholeParts(raw);
  return { text: parts ? `${parts.base}${parts.rest}` : raw, prefix, namedFragment };
}

/** The address as patterns are compared with it, built once per address rather than once per profile. */
function subjectOf(url: CanonicalParts | null, rawUrl: string): string {
  return url ? `${url.base}${url.rest}` : rawUrl;
}

function hits(pattern: CanonicalPattern, subject: string): boolean {
  return pattern.prefix ? subject.startsWith(pattern.text) : subject === pattern.text;
}

/**
 * Why a pattern should not be saved, or null when it can (TOADO-469): the
 * things a rule cannot mean because neither side of the match can see them.
 */
export function originPatternProblem(originPattern: string): string | null {
  if (originPattern.includes('#')) {
    return 'A capture profile cannot name the part of an address after "#". Browsers never send it, so the rule could not be applied.';
  }
  // Only a final `*` means anything: it makes the pattern a prefix. One
  // anywhere else - `https://*.bank.example/*`, or `/**` - is compared as a
  // literal star, so the rule matched nothing, which for a `never` rule is a
  // site the workspace believes it has blocked and has not. (A literal star in
  // a path is legal in a URL and the matcher could honour one; it is refused
  // all the same, as not worth the misreading it invites.)
  const star = originPattern.indexOf('*');
  if (star >= 0 && star !== originPattern.length - 1) {
    return 'A "*" can only be the last character. One "*" at the end already covers every page below that point, and each subdomain needs its own rule.';
  }
  // A lone `*` is a prefix of every address: a catch-all, and meant.
  if (originPattern === '*') return null;
  // What follows are rules no page could match, or rules that match other than
  // they read: each would be saved, shown in the list, and do nothing it says.
  const body = originPattern.endsWith('*') ? originPattern.slice(0, -1) : originPattern;
  const head = SCHEME.exec(body);
  if (!head || !/^https?$/i.test(head[1]!)) return 'Start the pattern with https:// or http://. The extension captures web pages only.';
  const after = body.slice(head[0].length);
  const end = after.search(AUTHORITY_END);
  const authority = end < 0 ? after : after.slice(0, end);
  if (end === 0) return 'Put the site name after https://.';
  // The matcher drops a user name and password, so `https://alice@example.com/x*`
  // covered every visitor's /x - and, longer than a site-wide rule, overrode it.
  if (authority.includes('@')) return 'Leave the user name and password out. A capture profile covers the site for everybody.';
  // No site name has a space or a percent sign. Chrome's parser takes
  // `https://not a url` as the host `not%20a%20url` where Node's refuses it,
  // so the form offered a rule the server then refused (TOADO-784). Said here,
  // the answer does not depend on whose parser reads it.
  if (/[\s%]/.test(authority)) return 'That is not a web address a browser could visit. Check the site name and port.';
  // A complete address must be one a browser could visit: a port past 65535 or
  // a malformed host is read by hand as written and matches nothing.
  const complete = end >= 0 || !originPattern.endsWith('*');
  if (complete && parseWhole(originPattern.endsWith('*') ? `${body}~` : body) === null) {
    return 'That is not a web address a browser could visit. Check the site name and port.';
  }
  if (!complete) return partialAuthorityProblem(head[1]!.toLowerCase(), after, body);
  return null;
}

/**
 * Why a site name cut off part way (`https://app.*`, `https://example.com:84*`)
 * cannot start an address a browser visits, or null when it can.
 *
 * Such a prefix is compared lowercased and otherwise as written, against
 * addresses in the form a browser gives them: an international name in its
 * xn-- form, a numeric one as dotted decimal (`0x7f` is 0.0.0.127), no trailing
 * dot, a port only when it is not the default. So it is accepted only when a
 * witness shows it can match: some address, completed from it and read the way
 * the matcher reads a visited one, that it is still the start of. That cannot
 * accept an inert prefix; at worst it refuses an odd one, and two kinds are
 * refused as policy - a partial IPv6 literal and a partial xn-- label - where
 * writing the name in full is clearer anyway.
 *
 * Review rounds 2 to 4 each found the version before too loose or too tight:
 * guessed endings that only proved some string parsed, then a character rule
 * that did not know the parser rewrites numeric and punycode names.
 */
function partialAuthorityProblem(scheme: string, after: string, body: string): string | null {
  if (/[^\x00-\x7f]/.test(after)) {
    return 'Write an international host name in its xn-- form, or end it with "/".';
  }
  const parts = /^([a-z0-9._-]*)(?::([0-9]*))?$/i.exec(after);
  if (!parts) {
    return 'A site name cut off part way can hold only letters, digits, dots, hyphens and underscores, then a port. Write an IPv6 address or an escaped name in full, ending with "/".';
  }
  const host = parts[1]!;
  const port = parts[2];
  if (host.startsWith('.') || host.includes('..')) return 'No site name starts with a dot or has two in a row. Check the dots.';
  const prefix = canonicalPrefix(body);
  const starts = (authority: string) => {
    const read = parseWhole(`${scheme}://${authority}/`);
    return read !== null && `${read.base}${read.rest}`.startsWith(prefix);
  };
  if (port !== undefined) {
    const witness = portWitness(port);
    if (witness === null) return 'No port starts that way. Ports run from 1 to 65535, with no leading zero.';
    if (!host || !starts(`${host}:${witness}`)) {
      return 'Write the site name before the port in full and as a browser shows it: no dot at its end, and a numeric address in dotted form.';
    }
    return null;
  }
  // The name as it stands, or carried on into a longer one - except a last
  // label that is punycode, which must be whole as written: carried on, one
  // extra letter can happen to make `xn--n3` valid (review round 5), and the
  // policy is that a partial xn-- label is refused.
  const punycodeLabel = /(^|\.)xn--[^.]*$/i.test(host);
  const witnesses = punycodeLabel ? [`${host}.example`, host] : [`${host}x.example`, `${host}.example`, host];
  if (!witnesses.some((authority) => authority !== '' && starts(authority))) {
    return 'No address a browser could visit starts that way. Write an international name\'s xn-- label in full, and a numeric address in dotted form.';
  }
  return null;
}

/** The first port a port prefix can finish as - itself, or with zeros added - or null. */
function portWitness(prefix: string): string | null {
  if (prefix === '') return '8443';
  for (let digits = prefix; digits.length <= 5; digits += '0') {
    const port = Number(digits);
    if (port >= 1 && port <= 65535 && !(digits.length > 1 && digits.startsWith('0'))) return digits;
  }
  return null;
}

/**
 * A pattern as the matcher reads it: the text it compares, canonical as a
 * visited address is (host lowercased, default port, user name and trailing
 * dots gone), and whether it is a prefix of addresses or one exact address.
 * The profile editor describes a pattern with this, so what it tells somebody
 * a rule covers is what the rule does - a copy of these rules in the editor
 * described what was typed instead, and they differed.
 */
export function readCapturePattern(originPattern: string): { text: string; prefix: boolean } {
  const { text, prefix } = patternOf(originPattern);
  return { text, prefix };
}

/**
 * The profile governing `url`, or null when none matches.
 *
 * `originPattern` is an exact URL, or a prefix ending in `*`. Of the profiles
 * that match, the longest prefix wins; between two of the same length the more
 * restrictive mode wins and every extra redaction either asks for applies, so
 * the order profiles happen to arrive in never decides what is collected. The
 * extra redactions of a tie were the winner's alone until review round 8: the
 * API reads profiles in no order, and a header one of two equal rules named
 * could be redacted by the extension and not by the server.
 */
export function matchCaptureProfile<T extends { originPattern: string; mode: string }>(
  profiles: readonly T[],
  url: string,
): T | null {
  let best: T | null = null;
  let bestLength = -1;
  let tied: T[] = [];
  const subject = subjectOf(wholeParts(url), url);
  const namingFragments: T[] = [];
  for (const profile of profiles) {
    if (typeof profile.originPattern !== 'string') continue;
    const pattern = patternOfProfile(profile);
    if (!hits(pattern, subject)) continue;
    if (pattern.namedFragment) {
      namingFragments.push(profile);
      continue;
    }
    const length = pattern.text.length;
    if (length > bestLength) {
      best = profile;
      bestLength = length;
      tied = [profile];
      continue;
    }
    if (length === bestLength) {
      tied.push(profile);
      if (best !== null && restrictiveness(profile) > restrictiveness(best)) best = profile;
    }
  }
  const decided = best !== null && tied.length > 1 ? tightenedBy(best, tied.filter((profile) => profile !== best)) : best;
  return namingFragments.length === 0 ? decided : tightenedBy(decided, namingFragments);
}

/**
 * The profiles that decide what is collected on at least one address of a
 * site, where `siteText` is `readCapturePattern(`${origin}/*`).text`.
 *
 * Every rule that starts inside the site can, since some address matches it
 * and nothing longer need. Of the rules that cover the whole site, only the
 * longest can (ties all kept): `matchCaptureProfile` prefers the longest
 * prefix that matches, so a shorter one never wins anywhere on the site.
 * Taking every rule that merely overlapped (second review, 2026-09-29) held a
 * workspace whose default is `*` `never`, with `full` for its own site, to
 * `never` on that site - where the matcher says `full` for every address.
 * A legacy rule naming a fragment only tightens, so any that could apply is
 * kept.
 */
export function profilesGoverningSite<T extends { originPattern: string; mode: string }>(
  profiles: readonly T[],
  siteText: string,
): T[] {
  const inside: T[] = [];
  let covering: T[] = [];
  let coveringLength = -1;
  for (const profile of profiles) {
    if (typeof profile.originPattern !== 'string') continue;
    const pattern = patternOfProfile(profile);
    const covers = pattern.prefix && siteText.startsWith(pattern.text);
    const within = pattern.text.startsWith(siteText);
    if (!covers && !within) continue;
    if (!covers || pattern.namedFragment) {
      inside.push(profile);
    } else if (pattern.text.length > coveringLength) {
      covering = [profile];
      coveringLength = pattern.text.length;
    } else if (pattern.text.length === coveringLength) {
      covering.push(profile);
    }
  }
  return [...covering, ...inside];
}

function restrictiveness(profile: { mode: string }): number {
  return RESTRICTIVENESS[captureProfileMode(profile.mode)];
}

type ExtraRedactions = { headers: string[]; urlParams: string[] };

function extrasOf(profile: object | null): ExtraRedactions {
  const extra = (profile as { extraRedactions?: unknown } | null)?.extraRedactions;
  const strings = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);
  if (!extra || typeof extra !== 'object') return { headers: [], urlParams: [] };
  const { headers, urlParams } = extra as { headers?: unknown; urlParams?: unknown };
  return { headers: strings(headers), urlParams: strings(urlParams) };
}

/**
 * What the other rules decided, tightened by the legacy rules that named a
 * fragment (see `patternOf`): the strictest mode among them, and every extra
 * redaction any of them asks for.
 *
 * Such a rule used to compete on length like any other, with only a `full`
 * one set aside, so a `metadata_only` rule for `/account#/billing` outranked a
 * broader `never` by being longer once its fragment was gone (TOADO-469, round
 * 5). Then, picked for its stricter mode, it replaced the other rule whole and
 * dropped the extra redactions that rule asked for (round 6). Nothing here can
 * now be looser than what the rules without a fragment decided, in either.
 */
function tightenedBy<T extends { originPattern: string; mode: string }>(best: T | null, namingFragments: readonly T[]): T {
  let mode = best ? captureProfileMode(best.mode) : 'full';
  const headers = new Set(extrasOf(best).headers);
  const urlParams = new Set(extrasOf(best).urlParams);
  for (const profile of namingFragments) {
    if (restrictiveness(profile) > RESTRICTIVENESS[mode]) mode = captureProfileMode(profile.mode);
    extrasOf(profile).headers.forEach((header) => headers.add(header));
    extrasOf(profile).urlParams.forEach((param) => urlParams.add(param));
  }
  return {
    ...(best ?? namingFragments[0]!),
    mode,
    extraRedactions: { headers: [...headers], urlParams: [...urlParams] },
  };
}
